/* Mobile TikTok Hero - Rebuilt v3.0
   - iOS/Safari friendly reels snapping
   - Plays only active slide video, pauses others
   - Pre-warms neighbors to reduce black start & "video-by-video" loading feel
   - Hysteresis thresholds prevent messy flicker during scroll
   - Uses stable viewport unit via --mth-vh (innerHeight based)
   - ✅ Chromium/Edge Android: smooth scroll-handoff (NO window.scrollBy, NO dead swipe)
*/

(() => {
  const TAG = "mobile-tiktok-hero";

  if (customElements.get(TAG)) return;

  class MobileTikTokHero extends HTMLElement {
    constructor() {
      super();

      // NOTE: on upgrade, children already exist (defer script), but we still re-query in connectedCallback.
      this.scroller = this.querySelector(".mobile-tiktok-hero__scroller");
      this.slides = Array.from(this.querySelectorAll("[data-slide]"));
      this.progress = this.querySelector(".mobile-tiktok-hero__progress");

      this.ratios = new Map();
      this.activeIndex = -1;
      this.observer = null;

      this._onVisibility = this._onVisibility.bind(this);
      this._onResize = this._onResize.bind(this);

      this._playStart = 0.68;
      this._playStop = 0.35;
      this._warmNeighbors = 1;
      this._autoplay = true;
      this._fadeIn = true;
      this._progressEnabled = false;
      this._progressStyle = "dots";

      this._handoffCleanup = null;
    }

    connectedCallback() {
      // Re-query for safety
      this.scroller = this.querySelector(".mobile-tiktok-hero__scroller");
      this.slides = Array.from(this.querySelectorAll("[data-slide]"));
      this.progress = this.querySelector(".mobile-tiktok-hero__progress");

      if (!this.scroller || !this.slides.length) return;

      // Read settings from attributes
      this._autoplay = this.getAttribute("data-autoplay") === "true";
      this._fadeIn = this.getAttribute("data-fade-in") === "true";

      // ✅ match schema: 0..5
      this._warmNeighbors = Math.max(
        0,
        Math.min(5, parseInt(this.getAttribute("data-warm-neighbors") || "1", 10))
      );

      const startRatio = parseFloat(this.getAttribute("data-start-ratio") || "0.68");
      const stopRatio = parseFloat(this.getAttribute("data-stop-ratio") || "0.35");
      this._playStart = Number.isFinite(startRatio) ? startRatio : 0.68;
      this._playStop = Number.isFinite(stopRatio) ? stopRatio : 0.35;

      this._progressEnabled = this.getAttribute("data-progress") === "true";
      this._progressStyle = this.getAttribute("data-progress-style") || "dots";

      // Stable viewport unit (fixes Safari toolbar resize glitches)
      this._setVH();
      window.addEventListener("resize", this._onResize, { passive: true });
      window.addEventListener("orientationchange", this._onResize, { passive: true });

      // Init ratio map
      this.slides.forEach((s) => this.ratios.set(s, 0));

      // Build progress UI
      if (this._progressEnabled && this.progress) {
        this._buildProgress();
      }

      // ✅ Chromium/Edge Android: enable smooth page scroll handoff at top/bottom edges
      this._enableScrollHandoff();

      this._initObserver();
      document.addEventListener("visibilitychange", this._onVisibility);

      // Respect reduced motion
      if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this._autoplay = false;
      }
    }

    disconnectedCallback() {
      document.removeEventListener("visibilitychange", this._onVisibility);
      window.removeEventListener("resize", this._onResize);
      window.removeEventListener("orientationchange", this._onResize);

      try {
        if (this._handoffCleanup) this._handoffCleanup();
      } catch (_) {}

      try {
        if (this.observer) this.observer.disconnect();
      } catch (_) {}

      this._pauseAll();
    }

    _enableScrollHandoff() {
      const scroller = this.scroller;
      if (!scroller) return;

      // Only apply on Android Chromium-family browsers (Chrome/Edge/Samsung/Opera)
      const ua = navigator.userAgent || "";
      const isAndroid = /Android/i.test(ua);
      const isChromiumFamily = /(Chrome|Chromium|EdgA|SamsungBrowser|OPR)/i.test(ua);
      if (!isAndroid || !isChromiumFamily) return;

      const EDGE_PX = 2;

      let lastY = null;
      let locked = false;
      let lockDir = 0; // 1 = bottom handoff, -1 = top handoff
      let unlockTimer = null;

      const maxScrollTop = () => scroller.scrollHeight - scroller.clientHeight;
      const atTopStrong = () => scroller.scrollTop <= EDGE_PX;
      const atBottomStrong = () => (maxScrollTop() - scroller.scrollTop) <= EDGE_PX;

      // Fallback if activeIndex lags during a fast fling
      const approxIndex = () => {
        const h = Math.max(1, scroller.clientHeight);
        return Math.round(scroller.scrollTop / h);
      };

      const isOnLast = () => {
        const last = this.slides.length - 1;
        return this.activeIndex === last || approxIndex() >= last;
      };

      const isOnFirst = () => {
        return this.activeIndex <= 0 || approxIndex() <= 0;
      };

      const lock = (dir) => {
        if (locked && lockDir === dir) return;
        locked = true;
        lockDir = dir;

        // Force exact edge to avoid scroll-snap "catch" / micro-pauses
        if (dir === 1) {
          scroller.scrollTop = maxScrollTop();
        } else {
          scroller.scrollTop = 0;
        }

        this.classList.add("mth-handoff");
      };

      const unlock = () => {
        if (!locked) return;
        locked = false;
        lockDir = 0;
        this.classList.remove("mth-handoff");
      };

      const scheduleUnlock = () => {
        if (unlockTimer) clearTimeout(unlockTimer);
        unlockTimer = setTimeout(() => {
          unlockTimer = null;
          unlock();
        }, 120);
      };

      const onStart = (e) => {
        if (!e.touches || !e.touches.length) return;
        lastY = e.touches[0].clientY;
        if (unlockTimer) {
          clearTimeout(unlockTimer);
          unlockTimer = null;
        }
      };

      const onMove = (e) => {
        if (!e.touches || !e.touches.length) return;

        const y = e.touches[0].clientY;
        const dy = (lastY == null) ? 0 : (lastY - y); // dy > 0 = user swiped up (wants to go down)
        lastY = y;

        if (!locked) {
          // ✅ No "dead swipe": lock immediately ONLY when truly at the edge AND on first/last slide
          if (dy > 0 && atBottomStrong() && isOnLast()) {
            lock(1);
            return;
          }
          if (dy < 0 && atTopStrong() && isOnFirst()) {
            lock(-1);
            return;
          }
          return;
        }

        // If user reverses direction while locked, give control back to inner scroller instantly
        if ((lockDir === 1 && dy < 0) || (lockDir === -1 && dy > 0)) {
          unlock();
          return;
        }

        scheduleUnlock();
      };

      const onEnd = () => {
        lastY = null;
        scheduleUnlock();
      };

      scroller.addEventListener("touchstart", onStart, { passive: true });
      scroller.addEventListener("touchmove", onMove, { passive: true });
      scroller.addEventListener("touchend", onEnd, { passive: true });
      scroller.addEventListener("touchcancel", onEnd, { passive: true });

      this._handoffCleanup = () => {
        if (unlockTimer) clearTimeout(unlockTimer);
        scroller.removeEventListener("touchstart", onStart);
        scroller.removeEventListener("touchmove", onMove);
        scroller.removeEventListener("touchend", onEnd);
        scroller.removeEventListener("touchcancel", onEnd);
      };
    }

    _onResize() {
      this._setVH();
      // Force re-evaluation after resize
      requestAnimationFrame(() => this._updateActive(true));
    }

    _setVH() {
      // Use innerHeight to avoid 100vh jumps on iOS Safari.
      const vh = window.innerHeight * 0.01;
      this.style.setProperty("--mth-vh", `${vh}px`);
    }

    _initObserver() {
      const thresholds = [0, 0.15, 0.25, 0.35, 0.5, 0.68, 0.8, 0.95, 1];

      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const r = entry.isIntersecting ? entry.intersectionRatio : 0;
            this.ratios.set(entry.target, r);
          }
          this._updateActive();
        },
        { root: this.scroller, threshold: thresholds }
      );

      this.slides.forEach((s) => this.observer.observe(s));
      requestAnimationFrame(() => this._updateActive(true));
    }

    _getVideo(slide) {
      const v = slide.querySelector("video.mth-video");
      if (!v) return null;

      const rects = v.getClientRects();
      if (!rects || !rects.length) return null;

      return v;
    }

    _warm(video) {
      try {
        video.muted = true;
        video.loop = true;
        video.controls = false;

        video.playsInline = true;
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");

        // Warm only once to avoid restarting decode
        if (!video.dataset.mthWarmed) {
          video.preload = "auto";
          video.load();
          video.dataset.mthWarmed = "1";
        } else {
          // Ensure it stays warm
          if (video.preload !== "auto") video.preload = "auto";
        }

        // Mark slide as ready when first frame is available
        if (this._fadeIn && !video.dataset.mthReadyHook) {
          const slideEl = video.closest(".mobile-tiktok-hero__slide");
          const onReady = () => {
            if (slideEl) slideEl.classList.add("is-ready");
          };
          video.addEventListener("loadeddata", onReady, { once: true });
          video.dataset.mthReadyHook = "1";
        }
      } catch (_) {}
    }

    _play(video) {
      if (!this._autoplay) return;
      try {
        const p = video.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch (_) {}
    }

    _pauseAll(except = null) {
      const all = Array.from(this.querySelectorAll("video.mth-video"));
      for (const v of all) {
        if (except && v === except) continue;
        try { if (!v.paused) v.pause(); } catch (_) {}
      }
    }

    _preloadNeighbors(index) {
      if (this._warmNeighbors <= 0) return;

      for (let offset = 1; offset <= this._warmNeighbors; offset++) {
        const prev = this.slides[index - offset];
        const next = this.slides[index + offset];

        [prev, next].forEach((slideEl) => {
          if (!slideEl) return;
          const v = this._getVideo(slideEl);
          if (!v) return;
          this._warm(v);
        });
      }
    }

    _updateProgress(index) {
      if (!this._progressEnabled || !this.progress) return;

      if (this._progressStyle === "bar") {
        const fill = this.progress.querySelector(".mth-bar-fill");
        if (!fill) return;
        const pct = this.slides.length <= 1 ? 100 : (index / (this.slides.length - 1)) * 100;
        fill.style.width = `${pct}%`;
        return;
      }

      // dots
      const dots = Array.from(this.progress.querySelectorAll(".mth-dot"));
      dots.forEach((d, i) => d.classList.toggle("is-active", i === index));
    }

    _buildProgress() {
      // Clean first
      this.progress.innerHTML = "";

      if (this._progressStyle === "bar") {
        this.progress.className = "mobile-tiktok-hero__progress is-bar";
        const fill = document.createElement("div");
        fill.className = "mth-bar-fill";
        this.progress.appendChild(fill);
        return;
      }

      this.progress.className = "mobile-tiktok-hero__progress is-dots";
      for (let i = 0; i < this.slides.length; i++) {
        const dot = document.createElement("div");
        dot.className = "mth-dot" + (i === 0 ? " is-active" : "");
        this.progress.appendChild(dot);
      }
    }

    _updateActive(force = false) {
      // Determine best slide by visibility ratio
      let bestSlide = null;
      let bestRatio = 0;

      for (const [slide, ratio] of this.ratios.entries()) {
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestSlide = slide;
        }
      }

      // If nothing is sufficiently visible, pause all
      if (!bestSlide || bestRatio < this._playStop) {
        this._pauseAll();
        this.activeIndex = -1;
        return;
      }

      const bestIndex = this.slides.indexOf(bestSlide);
      if (bestIndex === -1) return;

      // Hysteresis: keep current until it drops below stop, switch only when new is above start
      if (!force && this.activeIndex !== -1) {
        const currentSlide = this.slides[this.activeIndex];
        const currentRatio = this.ratios.get(currentSlide) || 0;

        // Keep current if still visible enough and new isn't strongly visible
        if (currentRatio >= this._playStop && bestRatio < this._playStart) {
          return;
        }

        // If switching, require best to be clearly visible
        if (bestIndex !== this.activeIndex && bestRatio < this._playStart) {
          return;
        }
      } else {
        // First activation requires start threshold
        if (!force && bestRatio < this._playStart) return;
      }

      if (bestIndex !== this.activeIndex || force) {
        this.activeIndex = bestIndex;

        const v = this._getVideo(bestSlide);
        if (!v) {
          this._pauseAll();
          this._updateProgress(bestIndex);
          return;
        }

        // Warm active + neighbors, then play active
        this._warm(v);
        this._preloadNeighbors(bestIndex);

        this._pauseAll(v);
        this._play(v);

        this._updateProgress(bestIndex);
      }
    }

    _onVisibility() {
      if (document.hidden) {
        this._pauseAll();
      } else {
        this._updateActive(true);
      }
    }
  }

  customElements.define(TAG, MobileTikTokHero);
})();
